import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Search,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Eye,
  Trash2,
  Download,
  Copy,
  Shield,
  AlertTriangle,
  RefreshCw,
  Bell,
  MessageCircle,
  Phone,
  DollarSign,
  UserPlus,
  X as CloseIcon,
  Calculator,
  Upload,
  Gift,
  Star,
  Calendar
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh, ENTITY_TYPES } from '../context/DataRefreshContext';
import { UserMenu } from '../components/UserMenu';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ModuleContainer } from '../components/ui/ModuleContainer';
import { SearchInput } from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/EmptyState';
import { ActionButton } from '../components/ui/ActionButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatear precio en formato colombiano
const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

// Estados de cotización
const ESTADOS = {
  borrador: { label: 'Borrador', color: 'bg-zinc-100 text-zinc-700', icon: FileText },
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-700', icon: Send },
  aprobada: { label: 'Aprobada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
  vencida: { label: 'Vencida', color: 'bg-orange-100 text-orange-700', icon: Clock }
};

// Filtros del Dashboard que agrupan varios estados (mismos criterios que sus contadores)
const FILTROS_GRUPO = {
  pendiente: { label: 'Pendientes (sin vender)' },
  venta: { label: 'Vendidas' },
};

const QuotesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyDeletion, refreshTimestamps } = useDataRefresh();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [filterEstado, setFilterEstado] = useState(() => searchParams.get('estado') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Estado para recordatorios
  const [recordatorios, setRecordatorios] = useState([]);
  const [showRecordatorios, setShowRecordatorios] = useState(false);
  const [loadingRecordatorios, setLoadingRecordatorios] = useState(false);
  
  // Estado para modal de confirmar venta
  const [showConfirmarVenta, setShowConfirmarVenta] = useState(false);
  const [cotizacionParaVenta, setCotizacionParaVenta] = useState(null);
  const [ventaConfig, setVentaConfig] = useState({
    modalidad_pago: 'pendiente_definir',
    porcentaje_anticipo: 70,
    num_cuotas: 2,
    // Fecha de entrega
    modalidad_fecha_entrega: 'automatica',
    fecha_entrega: '',
    ciudad_entrega: ''
  });
  const [confirmandoVenta, setConfirmandoVenta] = useState(false);
  
  // Estado para puntos JPM
  const [puntosDisponibles, setPuntosDisponibles] = useState(0);
  const [puntosAUsar, setPuntosAUsar] = useState(0);
  const [valorPorPunto, setValorPorPunto] = useState(100); // Default: 1 punto = $100
  const [usarPuntos, setUsarPuntos] = useState(false);
  const [cargandoPuntos, setCargandoPuntos] = useState(false);

  // Estado para borrado lógico (solo Gerencia)
  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [cotizacionParaEliminar, setCotizacionParaEliminar] = useState(null);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');
  const [confirmacionNumero, setConfirmacionNumero] = useState('');
  const [eliminando, setEliminando] = useState(false);
  const esGerencia = user?.role === 'gerencia' || user?.role === 'admin';

  // Cargar recordatorios
  const fetchRecordatorios = useCallback(async () => {
    setLoadingRecordatorios(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/quotes/recordatorios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordatorios(response.data.recordatorios || []);
    } catch (error) {
      console.error('Error al cargar recordatorios:', error);
    } finally {
      setLoadingRecordatorios(false);
    }
  }, []);

  // Cargar cotizaciones
  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterEstado) params.append('estado', filterEstado);
      
      const response = await axios.get(`${API_URL}/api/quotes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data.quotes);
    } catch (error) {
      toast.error('Error al cargar las cotizaciones');
    } finally {
      setLoading(false);
    }
  }, [search, filterEstado]);

  useEffect(() => {
    fetchQuotes();
    fetchRecordatorios();
  }, [fetchQuotes, fetchRecordatorios]);

  // Refrescar datos cuando otra vista invalide el caché de cotizaciones
  useEffect(() => {
    fetchQuotes();
  }, [refreshTimestamps.cotizaciones]);

  // Buscar con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotes]);

  // Eliminar cotización
  const handleDelete = async (id, numero, quote) => {
    // Solo Gerencia usa borrado lógico con modal
    if (esGerencia) {
      setCotizacionParaEliminar({ id, numero, ...quote });
      setMotivoEliminacion('');
      setConfirmacionNumero('');
      setShowEliminarModal(true);
    } else {
      // Otros roles: eliminación simple (si tienen permiso)
      if (!window.confirm(`¿Eliminar la cotización ${numero}?`)) return;
      
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/api/quotes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cotización eliminada');
        fetchQuotes();
      } catch (error) {
        toast.error('Error al eliminar la cotización');
      }
    }
  };

  const confirmarEliminacionCotizacion = async () => {
    if (!cotizacionParaEliminar) return;
    
    if (!motivoEliminacion.trim() || motivoEliminacion.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    if (confirmacionNumero.trim().toUpperCase() !== cotizacionParaEliminar.numero.toUpperCase()) {
      toast.error('El número de cotización no coincide');
      return;
    }
    
    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/gerencia/cotizacion/${cotizacionParaEliminar.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          motivo: motivoEliminacion,
          confirmar_nombre: confirmacionNumero
        }
      });
      toast.success('Cotización eliminada lógicamente. Puede restaurarse desde Registros Eliminados.');
      setShowEliminarModal(false);
      setCotizacionParaEliminar(null);
      
      // Notificar invalidación de caché para actualizar todas las vistas
      notifyDeletion(ENTITY_TYPES.COTIZACIONES, { cliente_id: cotizacionParaEliminar.cliente_id });
      
      fetchQuotes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al eliminar cotización');
    } finally {
      setEliminando(false);
    }
  };

  // Duplicar cotización
  const handleDuplicate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/quotes/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cotización duplicada: ${response.data.numero}`);
      navigate(`/quotes/${response.data.id}`);
    } catch (error) {
      toast.error('Error al duplicar la cotización');
    }
  };

  // Descargar PDF
  const handleDownloadPDF = async (id, numero) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar el PDF');
    }
  };

  // Limpieza manual de cotizaciones antiguas (solo GERENCIA)
  const handleManualCleanup = async () => {
    if (!window.confirm('¿Eliminar todas las cotizaciones con más de 60 días sin ficha de pedido asociada?')) return;
    
    setCleaningUp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/quotes/cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar limpieza');
    } finally {
      setCleaningUp(false);
    }
  };

  // Estadísticas rápidas
  const stats = {
    total: quotes.length,
    borradores: quotes.filter(q => q.estado === 'borrador').length,
    enviadas: quotes.filter(q => q.estado === 'enviada').length,
    aprobadas: quotes.filter(q => q.estado === 'aprobada').length,
    valorTotal: quotes.reduce((sum, q) => sum + (q.total || 0), 0)
  };

  // Enviar por WhatsApp
  const handleSendWhatsApp = async (quoteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/quotes/${quoteId}/enviar-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.open(response.data.whatsapp_url, '_blank');
      toast.success('WhatsApp abierto con enlace al PDF');
      fetchQuotes(); // Recargar para actualizar estado
    } catch (error) {
      toast.error('Error al preparar envío por WhatsApp');
    }
  };

  // Abrir modal para confirmar venta
  const handleMarcarVenta = async (quoteId, numero) => {
    // Buscar la cotización para mostrar sus datos
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) {
      toast.error('Cotización no encontrada');
      return;
    }
    setCotizacionParaVenta(quote);
    setVentaConfig({
      modalidad_pago: 'pendiente_definir',
      porcentaje_anticipo: 70,
      num_cuotas: 2,
      modalidad_fecha_entrega: 'automatica',
      fecha_entrega: '',
      ciudad_entrega: ''
    });
    
    // Reset de puntos
    setPuntosDisponibles(0);
    setPuntosAUsar(0);
    setUsarPuntos(false);
    
    // Consultar puntos disponibles si el cliente tiene ID
    if (quote.cliente_id) {
      setCargandoPuntos(true);
      try {
        const token = localStorage.getItem('token');
        // Obtener saldo de puntos
        const saldoResponse = await axios.get(`${API_URL}/api/puntos-jpm/cliente/${quote.cliente_id}/saldo`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPuntosDisponibles(saldoResponse.data.saldo_puntos || 0);
        
        // Obtener configuración del programa (valor por punto)
        const configResponse = await axios.get(`${API_URL}/api/puntos-jpm/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setValorPorPunto(configResponse.data.valor_redencion_punto || 100);
      } catch (error) {
        console.error('Error al consultar puntos:', error);
        setPuntosDisponibles(0);
      } finally {
        setCargandoPuntos(false);
      }
    }
    
    setShowConfirmarVenta(true);
  };
  
  // Confirmar la venta y crear orden con plan de pagos
  const confirmarVentaFinal = async () => {
    if (!cotizacionParaVenta) return;
    
    // Validar que no se usen más puntos de los disponibles
    if (usarPuntos && puntosAUsar > puntosDisponibles) {
      toast.error('No puedes usar más puntos de los disponibles');
      return;
    }
    
    // Validar fecha de entrega si es manual
    if (ventaConfig.modalidad_fecha_entrega === 'manual' && !ventaConfig.fecha_entrega) {
      toast.error('Ingresa la fecha de entrega');
      return;
    }
    
    setConfirmandoVenta(true);
    try {
      const token = localStorage.getItem('token');
      
      // Calcular descuento por puntos
      const descuentoPuntos = usarPuntos ? puntosAUsar * valorPorPunto : 0;
      const totalConDescuento = Math.max(0, cotizacionParaVenta.total - descuentoPuntos);
      
      // Llamar al endpoint correcto que crea la orden de venta con plan de pagos
      const response = await axios.post(`${API_URL}/api/ventas/confirmar`, {
        cotizacion_id: cotizacionParaVenta.id,
        modalidad_pago: ventaConfig.modalidad_pago,
        porcentaje_anticipo: ventaConfig.porcentaje_anticipo,
        num_cuotas: ventaConfig.num_cuotas,
        // Datos de puntos a canjear
        puntos_a_canjear: usarPuntos ? puntosAUsar : 0,
        descuento_por_puntos: descuentoPuntos,
        // Fecha de entrega
        modalidad_fecha_entrega: ventaConfig.modalidad_fecha_entrega,
        fecha_entrega: ventaConfig.modalidad_fecha_entrega === 'manual' ? ventaConfig.fecha_entrega : null,
        ciudad_entrega: ventaConfig.ciudad_entrega || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`¡Venta confirmada! Orden: ${response.data.orden?.numero || 'Creada'}`);
      
      if (response.data.orden?.plan_pagos?.length > 0) {
        toast.info(`Plan de pagos creado con ${response.data.orden.plan_pagos.length} cuota(s)`);
      }
      
      // Mostrar info de puntos canjeados
      if (response.data.puntos_canjeados > 0) {
        toast.success(`🎁 ${response.data.puntos_canjeados} puntos canjeados (${formatPrice(response.data.descuento_aplicado)} de descuento)`);
      }
      
      setShowConfirmarVenta(false);
      setCotizacionParaVenta(null);
      setPuntosAUsar(0);
      setUsarPuntos(false);
      fetchQuotes();
      fetchRecordatorios();
      
      // FLUJO AUTOMÁTICO: Preguntar si desea crear ficha de confección
      const ordenId = response.data.orden?.id;
      const clienteId = cotizacionParaVenta.cliente_id;
      const clienteNombre = cotizacionParaVenta.cliente_nombre;
      
      // Usar setTimeout para que el toast se muestre primero
      setTimeout(() => {
        const crearFicha = window.confirm(
          `¡Venta confirmada exitosamente!\n\n¿Desea crear la Ficha de Confección para ${clienteNombre}?\n\n(La ficha permite registrar medidas y detalles del pedido)`
        );
        
        if (crearFicha) {
          // Navegar a crear nueva ficha con el cliente preseleccionado
          navigate(`/sheet/new?cliente_id=${clienteId}&cliente_nombre=${encodeURIComponent(clienteNombre)}&orden_id=${ordenId || ''}`);
        }
      }, 500);
    } catch (error) {
      const detalle = error.response?.data?.detail || '';
      if (error.response?.status === 400 && detalle.includes('ya tiene una orden')) {
        toast.info('Esta cotización ya fue vendida. Actualizamos la lista.');
        setShowConfirmarVenta(false);
        setCotizacionParaVenta(null);
        fetchQuotes();
      } else {
        toast.error(detalle || 'Error al confirmar la venta');
      }
    } finally {
      setConfirmandoVenta(false);
    }
  };
  
  // Calcular total con descuento de puntos
  const calcularTotalConPuntos = () => {
    if (!cotizacionParaVenta) return 0;
    const descuento = usarPuntos ? puntosAUsar * valorPorPunto : 0;
    return Math.max(0, cotizacionParaVenta.total - descuento);
  };
  
  // Calcular máximo de puntos utilizables (no más que el total de la cotización)
  const calcularMaxPuntos = () => {
    if (!cotizacionParaVenta) return 0;
    const maxPorTotal = Math.floor(cotizacionParaVenta.total / valorPorPunto);
    return Math.min(puntosDisponibles, maxPorTotal);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Modal de Recordatorios */}
      {showRecordatorios && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6" />
                <h2 className="font-serif text-xl">Recordatorios de Seguimiento</h2>
              </div>
              <button onClick={() => setShowRecordatorios(false)} className="p-1 hover:bg-white/20 rounded">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {loadingRecordatorios ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : recordatorios.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p className="font-medium text-slate-900">¡Todo al día!</p>
                  <p className="text-sm">No hay recordatorios pendientes.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recordatorios.map((rec, idx) => (
                    <div
                      key={rec.quote_id || rec.ficha_id || `rec-${rec.tipo}-${idx}`}
                      className={`p-4 rounded-xl border-l-4 ${
                        rec.prioridad === 'alta' 
                          ? 'bg-red-50 border-red-500' 
                          : rec.prioridad === 'media'
                          ? 'bg-amber-50 border-amber-500'
                          : 'bg-zinc-50 border-zinc-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {rec.tipo === 'sin_respuesta' && <Clock className="w-4 h-4 text-blue-600" />}
                            {rec.tipo === 'proxima_vencer' && <AlertTriangle className="w-4 h-4 text-orange-600" />}
                            {rec.tipo === 'venta_sin_ficha' && <UserPlus className="w-4 h-4 text-green-600" />}
                            <span className="font-medium">{rec.numero}</span>
                            <StatusBadge 
                              status={rec.prioridad === 'alta' ? 'error' : 'warning'} 
                              type="default"
                              customLabel={rec.prioridad === 'alta' ? 'URGENTE' : 'Pendiente'}
                              size="sm"
                              showIcon={false}
                            />
                          </div>
                          <p className="font-serif text-lg text-slate-900">{rec.cliente}</p>
                          <p className="text-sm text-slate-600">{rec.mensaje}</p>
                          <p className="text-sm font-medium text-amber-600 mt-1">{formatPrice(rec.valor)}</p>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <ActionButton
                            onClick={() => navigate(`/quotes/${rec.quote_id}`)}
                            variant="primary"
                            size="sm"
                            icon={Eye}
                          >
                            Ver
                          </ActionButton>
                          {rec.telefono && (
                            <ActionButton
                              onClick={() => handleSendWhatsApp(rec.quote_id)}
                              variant="success"
                              size="sm"
                              icon={MessageCircle}
                            >
                              WhatsApp
                            </ActionButton>
                          )}
                          {rec.tipo === 'venta_sin_ficha' && (
                            <ActionButton
                              onClick={() => handleMarcarVenta(rec.quote_id, rec.numero)}
                              variant="gold"
                              size="sm"
                              icon={UserPlus}
                            >
                              Ficha
                            </ActionButton>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
              <div className="text-sm text-slate-500">
                {recordatorios.length} pendiente{recordatorios.length !== 1 ? 's' : ''}
              </div>
              <ActionButton
                onClick={() => { fetchRecordatorios(); }}
                variant="primary"
                size="sm"
                icon={RefreshCw}
              >
                Actualizar
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmar Venta */}
      {showConfirmarVenta && cotizacionParaVenta && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6" />
                <h2 className="font-serif text-xl">Confirmar Venta</h2>
              </div>
              <button 
                onClick={() => { setShowConfirmarVenta(false); setCotizacionParaVenta(null); }} 
                className="p-1 hover:bg-white/20 rounded"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Info de la cotización */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Cotización</p>
                <p className="font-mono font-bold text-lg">{cotizacionParaVenta.numero}</p>
                <p className="text-slate-700">{cotizacionParaVenta.cliente_nombre}</p>
                <p className="text-emerald-600 font-semibold text-lg mt-2">
                  {formatPrice(cotizacionParaVenta.total)}
                </p>
              </div>
              
              {/* Configuración del plan de pagos */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Plan de Pagos
                </h3>
                
                {/* Modalidad */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Modalidad</label>
                  <select
                    value={ventaConfig.modalidad_pago}
                    onChange={(e) => setVentaConfig({...ventaConfig, modalidad_pago: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="modalidad-pago-select"
                  >
                    <option value="pendiente_definir">Pendiente por definir</option>
                    <option value="pago_total">Contado (100%)</option>
                    <option value="anticipo_saldo">Anticipo + Saldo</option>
                    <option value="pago_plazos">Cuotas</option>
                  </select>
                  {ventaConfig.modalidad_pago === 'pendiente_definir' && (
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Podrás definir el plan de pagos después en la orden de venta
                    </p>
                  )}
                </div>
                
                {/* Porcentaje anticipo (solo si es anticipo_saldo) */}
                {ventaConfig.modalidad_pago === 'anticipo_saldo' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Porcentaje de Anticipo: {ventaConfig.porcentaje_anticipo}%
                    </label>
                    <input
                      type="range"
                      min="30"
                      max="90"
                      step="5"
                      value={ventaConfig.porcentaje_anticipo}
                      onChange={(e) => setVentaConfig({...ventaConfig, porcentaje_anticipo: parseInt(e.target.value)})}
                      className="w-full accent-emerald-500"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>Anticipo: {formatPrice(cotizacionParaVenta.total * ventaConfig.porcentaje_anticipo / 100)}</span>
                      <span>Saldo: {formatPrice(cotizacionParaVenta.total * (100 - ventaConfig.porcentaje_anticipo) / 100)}</span>
                    </div>
                  </div>
                )}
                
                {/* Número de cuotas (solo si es pago_plazos) */}
                {ventaConfig.modalidad_pago === 'pago_plazos' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Número de Cuotas</label>
                    <input
                      type="number"
                      min="2"
                      max="24"
                      value={ventaConfig.num_cuotas}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 2;
                        setVentaConfig({...ventaConfig, num_cuotas: Math.max(2, Math.min(24, val))});
                      }}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Ej: 2, 3, 4, 6, 12..."
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Cada cuota: {formatPrice(calcularTotalConPuntos() / ventaConfig.num_cuotas)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Sección de Usar Puntos JPM */}
              {cotizacionParaVenta.cliente_id && (
                <div className="space-y-3">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <Star className="w-5 h-5 text-purple-500" />
                    Puntos JPM
                  </h3>
                  
                  {cargandoPuntos ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <div className="w-4 h-4 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
                      Consultando puntos disponibles...
                    </div>
                  ) : puntosDisponibles > 0 ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                      {/* Toggle usar puntos */}
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm font-medium text-slate-700">
                          Usar puntos como descuento
                        </span>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={usarPuntos}
                            onChange={(e) => {
                              setUsarPuntos(e.target.checked);
                              if (!e.target.checked) {
                                setPuntosAUsar(0);
                              }
                            }}
                            className="sr-only"
                            data-testid="toggle-usar-puntos"
                          />
                          <div className={`w-11 h-6 rounded-full transition-colors ${usarPuntos ? 'bg-purple-500' : 'bg-slate-300'}`}>
                            <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${usarPuntos ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'}`} />
                          </div>
                        </div>
                      </label>
                      
                      {/* Puntos disponibles */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Puntos disponibles:</span>
                        <span className="font-bold text-purple-600">{puntosDisponibles.toLocaleString()} pts</span>
                      </div>
                      
                      {/* Selector de puntos a usar */}
                      {usarPuntos && (
                        <>
                          <div>
                            <label className="block text-sm text-slate-600 mb-1">
                              Puntos a canjear (máx: {calcularMaxPuntos().toLocaleString()}):
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max={calcularMaxPuntos()}
                                value={puntosAUsar}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  setPuntosAUsar(Math.min(val, calcularMaxPuntos()));
                                }}
                                className="flex-1 border border-purple-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                data-testid="input-puntos-canjear"
                              />
                              <button
                                onClick={() => setPuntosAUsar(calcularMaxPuntos())}
                                className="px-3 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                              >
                                Máx
                              </button>
                            </div>
                          </div>
                          
                          {/* Equivalencia y resumen */}
                          {puntosAUsar > 0 && (
                            <div className="bg-white rounded-lg p-3 space-y-2 border border-purple-200">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">Descuento por puntos:</span>
                                <span className="font-bold text-green-600">- {formatPrice(puntosAUsar * valorPorPunto)}</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">Puntos restantes después:</span>
                                <span className="font-medium text-purple-600">{(puntosDisponibles - puntosAUsar).toLocaleString()} pts</span>
                              </div>
                              <div className="pt-2 border-t border-purple-100 flex items-center justify-between">
                                <span className="font-medium text-slate-700">Total a pagar:</span>
                                <span className="font-bold text-lg text-emerald-600">{formatPrice(calcularTotalConPuntos())}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      <p className="text-xs text-purple-600">
                        💡 1 punto = {formatPrice(valorPorPunto)} de descuento
                      </p>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-500">
                      Este cliente no tiene puntos JPM disponibles.
                    </div>
                  )}
                </div>
              )}
              
              {/* Resumen final si hay puntos */}
              {usarPuntos && puntosAUsar > 0 && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-medium text-emerald-800 mb-2 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Resumen de la Venta
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Subtotal:</span>
                      <span>{formatPrice(cotizacionParaVenta.total)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Descuento ({puntosAUsar.toLocaleString()} puntos):</span>
                      <span>- {formatPrice(puntosAUsar * valorPorPunto)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-emerald-200">
                      <span className="text-emerald-800">TOTAL A PAGAR:</span>
                      <span className="text-emerald-600">{formatPrice(calcularTotalConPuntos())}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Fecha de Entrega */}
              <div className="space-y-3">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Fecha de Entrega
                </h3>
                
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                  {/* Selector de modalidad */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setVentaConfig({...ventaConfig, modalidad_fecha_entrega: 'automatica', fecha_entrega: ''})}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        ventaConfig.modalidad_fecha_entrega === 'automatica'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${
                        ventaConfig.modalidad_fecha_entrega === 'automatica' ? 'text-blue-700' : 'text-slate-700'
                      }`}>
                        Automática
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        8 días antes del evento
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setVentaConfig({...ventaConfig, modalidad_fecha_entrega: 'manual'})}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        ventaConfig.modalidad_fecha_entrega === 'manual'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${
                        ventaConfig.modalidad_fecha_entrega === 'manual' ? 'text-amber-700' : 'text-slate-700'
                      }`}>
                        Manual
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Fecha personalizada
                      </p>
                    </button>
                  </div>
                  
                  {/* Input de fecha manual */}
                  {ventaConfig.modalidad_fecha_entrega === 'manual' && (
                    <div>
                      <label className="block text-sm text-slate-600 mb-1">Fecha de entrega *</label>
                      <input
                        type="date"
                        value={ventaConfig.fecha_entrega}
                        onChange={(e) => setVentaConfig({...ventaConfig, fecha_entrega: e.target.value})}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        data-testid="fecha-entrega-input"
                      />
                    </div>
                  )}
                  
                  {/* Información de fecha automática */}
                  {ventaConfig.modalidad_fecha_entrega === 'automatica' && (
                    <p className="text-xs text-blue-600">
                      {cotizacionParaVenta.fecha_evento ? (
                        `Se calculará automáticamente desde la fecha del evento: ${new Date(cotizacionParaVenta.fecha_evento).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      ) : (
                        'La cotización no tiene fecha de evento. Se recomienda usar fecha manual.'
                      )}
                    </p>
                  )}
                  
                  {/* Ciudad de entrega (opcional) */}
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Ciudad de entrega (opcional)</label>
                    <input
                      type="text"
                      value={ventaConfig.ciudad_entrega}
                      onChange={(e) => setVentaConfig({...ventaConfig, ciudad_entrega: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ej: Bogotá, Medellín..."
                      data-testid="ciudad-entrega-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Aviso */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-700 text-sm">
                  Se creará una orden de venta con plan de pagos. El cliente quedará registrado y podrá acceder a "Mi JPM".
                  {usarPuntos && puntosAUsar > 0 && (
                    <span className="block mt-1 text-purple-700">
                      🎁 Los {puntosAUsar.toLocaleString()} puntos se descontarán del saldo del cliente al confirmar.
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex gap-3">
              <button
                onClick={() => { setShowConfirmarVenta(false); setCotizacionParaVenta(null); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                data-testid="cancelar-venta-btn"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarVentaFinal}
                disabled={confirmandoVenta}
                className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirmar-venta-btn"
              >
                {confirmandoVenta ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmar Venta
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header con Safe Area para iOS */}
      <header className="bg-black/95 backdrop-blur-xl text-white fixed top-0 left-0 w-full z-50 border-b border-zinc-800 shadow-lg safe-area-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Mobile: 2 rows */}
          <div className="flex flex-col gap-2 py-3 md:hidden">
            {/* Row 1: Back + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-shrink-0 flex items-center gap-1 text-white/70 hover:text-white transition-colors select-none"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-4 h-4 text-sky-400" />
                </div>
                <h1 className="font-serif text-lg truncate">Cotizaciones</h1>
              </div>
              {/* Recordatorios badge on mobile */}
              <button
                onClick={() => setShowRecordatorios(true)}
                className="relative flex-shrink-0 w-10 h-10 flex items-center justify-center bg-amber-600 rounded-lg"
                data-testid="recordatorios-btn-mobile"
              >
                <Bell className="w-5 h-5" />
                {recordatorios.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {recordatorios.length > 9 ? '9+' : recordatorios.length}
                  </span>
                )}
              </button>
            </div>
            {/* Row 2: Actions */}
            <div className="flex items-center gap-2">
              <ActionButton
                onClick={() => navigate('/quotes/new')}
                variant="gold"
                icon={Plus}
                className="flex-1 justify-center"
                data-testid="new-quote-btn-mobile"
              >
                Nueva Cotización
              </ActionButton>
            </div>
          </div>
          
          {/* Desktop: Single row */}
          <div className="hidden md:flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Dashboard</span>
              </button>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-sky-500/20 flex items-center justify-center">
                  <Calculator className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <h1 className="font-serif text-lg">Cotizaciones</h1>
                  <p className="text-[10px] text-white/50 uppercase tracking-wider">Propuestas y ventas</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Botón de recordatorios */}
              <button
                onClick={() => setShowRecordatorios(true)}
                className="relative flex items-center gap-2 bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 transition-colors text-sm"
                title="Ver recordatorios"
                data-testid="recordatorios-btn"
              >
                <Bell className="w-4 h-4" />
                <span>Recordatorios</span>
                {recordatorios.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {recordatorios.length > 9 ? '9+' : recordatorios.length}
                  </span>
                )}
              </button>
              
              {/* Botón de limpieza manual - solo GERENCIA */}
              {user?.role === 'gerencia' && (
                <ActionButton
                  onClick={handleManualCleanup}
                  disabled={cleaningUp}
                  variant="ghost"
                  size="sm"
                  icon={RefreshCw}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  {cleaningUp ? 'Limpiando...' : 'Limpiar antiguas'}
                </ActionButton>
              )}
              
              {/* Botón de cargar tarifas - solo admin/gerencia */}
              {(user?.role === 'admin' || user?.role === 'gerencia') && (
                <ActionButton
                  onClick={() => navigate('/cotizador/cargar-tarifas')}
                  variant="ghost"
                  size="sm"
                  icon={Upload}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                  data-testid="btn-cargar-tarifas"
                >
                  Cargar Tarifas
                </ActionButton>
              )}
              
              <ActionButton
                onClick={() => navigate('/quotes/new')}
                variant="gold"
                icon={Plus}
                data-testid="new-quote-btn"
              >
                Nueva Cotización
              </ActionButton>
              
              {/* User Menu */}
              <UserMenu variant="dark" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 content-below-safe-header">
        {/* Stats Cards */}
        <ModuleContainer variant="cotizaciones" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Total</p>
              <p className="text-2xl font-serif mt-1 text-slate-900">{stats.total}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Borradores</p>
              <p className="text-2xl font-serif mt-1 text-slate-600">{stats.borradores}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-sky-100 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-sky-600 font-medium">Enviadas</p>
              <p className="text-2xl font-serif mt-1 text-sky-600">{stats.enviadas}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-emerald-100 shadow-sm">
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Aprobadas</p>
              <p className="text-2xl font-serif mt-1 text-emerald-600">{stats.aprobadas}</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-amber-100 shadow-sm col-span-2 md:col-span-1">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-medium">Valor Total</p>
              <p className="text-xl font-serif mt-1 text-amber-600">{formatPrice(stats.valorTotal)}</p>
            </div>
          </div>
        </ModuleContainer>

        {/* Search and Filters */}
        <ModuleContainer variant="cotizaciones" className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número, cliente o correo..."
              className="flex-1"
            />
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-colors ${
                showFilters ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="filter-toggle"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>
          
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3">Filtrar por estado</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterEstado('')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    filterEstado === '' ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                  data-testid="filter-all"
                >
                  Todos
                </button>
                {Object.entries({ ...FILTROS_GRUPO, ...ESTADOS }).map(([key, { label }]) => (
                  <button
                    key={key}
                    onClick={() => setFilterEstado(key)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      filterEstado === key ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                    data-testid={`filter-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </ModuleContainer>

        {/* Quotes List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-2 border-sky-200 border-t-sky-600 rounded-full animate-spin"></div>
          </div>
        ) : quotes.length === 0 ? (
          <ModuleContainer variant="cotizaciones">
            <EmptyState
              type={search || filterEstado ? 'search' : 'cotizaciones'}
              title={search || filterEstado ? 'No se encontraron resultados' : 'No hay cotizaciones'}
              description={
                search || filterEstado 
                  ? 'No se encontraron resultados con los filtros aplicados' 
                  : 'Crea tu primera cotización para comenzar a gestionar propuestas'
              }
              actionLabel={search || filterEstado ? 'Limpiar filtros' : 'Nueva Cotización'}
              onAction={search || filterEstado ? () => { setSearch(''); setFilterEstado(''); } : () => navigate('/quotes/new')}
            />
          </ModuleContainer>
        ) : (
          <div className="space-y-4">
            {/* Contador de resultados */}
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-slate-500">
                Mostrando <span className="font-medium text-slate-900">{quotes.length}</span> {quotes.length === 1 ? 'cotización' : 'cotizaciones'}
                {filterEstado && <span> en estado "{(FILTROS_GRUPO[filterEstado] || ESTADOS[filterEstado])?.label}"</span>}
              </p>
            </div>
            
            {quotes.map((quote) => {
              const estadoInfo = ESTADOS[quote.estado] || ESTADOS.borrador;
              const EstadoIcon = estadoInfo.icon;
              const isProtected = quote.is_protected;
              const daysUntilDeletion = quote.days_until_deletion ?? 60;
              const isExpiringSoon = !isProtected && daysUntilDeletion <= 15;
              
              return (
                <div
                  key={quote.id}
                  className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-all ${
                    isProtected ? 'border-emerald-200 border-l-4 border-l-emerald-500' : isExpiringSoon ? 'border-orange-200 border-l-4 border-l-orange-500' : 'border-slate-200 border-l-4 border-l-sky-500'
                  }`}
                  data-testid={`quote-card-${quote.id}`}
                >
                  <div className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {/* Info principal */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-serif text-xl text-slate-900">{quote.cliente_nombre}</h3>
                          <StatusBadge status={quote.estado} type="cotizacion" />
                          
                          {/* Indicador de protección o días restantes */}
                          {isProtected ? (
                            <StatusBadge status="protegida" type="default" customLabel="Protegida" showIcon={true} />
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                              isExpiringSoon ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {isExpiringSoon && <AlertTriangle className="w-3 h-3" />}
                              {daysUntilDeletion > 0 ? `${daysUntilDeletion} días` : 'Expira hoy'}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="font-medium text-slate-900">{quote.numero}</span>
                          <span>{quote.items?.length || 0} items</span>
                          <span>Creada: {quote.fecha_creacion?.substring(0, 10)}</span>
                          {quote.fecha_vencimiento && (
                            <span>Vence: {quote.fecha_vencimiento.substring(0, 10)}</span>
                          )}
                          {quote.asesor_comercial && (
                            <span>Asesor: {quote.asesor_comercial}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Precio */}
                      <div className="text-right">
                        <p className="text-2xl font-display text-[#D4AF37]">{formatPrice(quote.total)}</p>
                        {quote.descuento > 0 && (
                          <p className="text-sm text-muted-foreground">
                            Desc: {formatPrice(quote.descuento)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Acciones - Mejorado para móvil */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
                      <button
                        onClick={() => navigate(`/quotes/${quote.id}`)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-black text-white rounded-lg hover:bg-zinc-800 transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden xs:inline">Ver / Editar</span>
                        <span className="xs:hidden">Ver</span>
                      </button>
                      
                      <button
                        onClick={() => handleSendWhatsApp(quote.id)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
                        title="Enviar por WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                      
                      {quote.orden_venta_id && (
                        <span
                          className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs sm:text-sm font-medium min-h-[44px] sm:min-h-0"
                          title={quote.orden_venta_numero ? `Orden ${quote.orden_venta_numero}` : 'Venta registrada'}
                          data-testid={`ya-vendida-${quote.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Ya vendida{quote.orden_venta_numero ? ` · ${quote.orden_venta_numero}` : ''}
                        </span>
                      )}

                      {/* Botón "Registrar Venta" - Destacado si cotización aprobada */}
                      {!quote.orden_venta_id && !quote.convertida_a_venta && (
                        <button
                          onClick={() => handleMarcarVenta(quote.id, quote.numero)}
                          className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0 ${
                            quote.estado === 'aprobada' 
                              ? 'bg-emerald-500 text-white hover:bg-emerald-600 ring-2 ring-emerald-300 animate-pulse-subtle' 
                              : 'bg-[#D4AF37] text-black hover:bg-[#c4a030]'
                          }`}
                          title={quote.estado === 'aprobada' ? '¡Cotización lista! Clic para registrar venta' : 'Marcar como venta realizada'}
                          data-testid={`marcar-venta-${quote.id}`}
                        >
                          <DollarSign className="w-4 h-4" />
                          <span className="hidden sm:inline">{quote.estado === 'aprobada' ? 'Registrar Venta' : 'Marcar Venta'}</span>
                          <span className="sm:hidden">{quote.estado === 'aprobada' ? 'Venta' : 'Venta'}</span>
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDownloadPDF(quote.id, quote.numero)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 border border-border rounded-lg hover:bg-zinc-50 transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
                      >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">PDF</span>
                      </button>
                      
                      <button
                        onClick={() => handleDuplicate(quote.id)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 border border-border rounded-lg hover:bg-zinc-50 transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
                      >
                        <Copy className="w-4 h-4" />
                        <span className="hidden sm:inline">Duplicar</span>
                      </button>
                      
                      <button
                        onClick={() => handleDelete(quote.id, quote.numero, quote)}
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm min-h-[44px] sm:min-h-0"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Eliminar Cotización (Borrado Lógico - Solo Gerencia) */}
        {showEliminarModal && cotizacionParaEliminar && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold flex items-center gap-2 text-red-600">
                    <Trash2 className="w-5 h-5" />
                    Eliminar Cotización
                  </h2>
                  <button
                    onClick={() => {
                      setShowEliminarModal(false);
                      setCotizacionParaEliminar(null);
                    }}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Info de la cotización */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Cotización a eliminar</p>
                  <p className="font-semibold text-lg">{cotizacionParaEliminar.numero}</p>
                  <p className="text-sm text-slate-600">{cotizacionParaEliminar.cliente_nombre}</p>
                  <p className="text-sm font-medium text-slate-700">
                    Total: {formatPrice(cotizacionParaEliminar.total)}
                  </p>
                </div>
                
                {/* Formulario */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Motivo de eliminación * (mín. 10 caracteres)
                  </label>
                  <textarea
                    value={motivoEliminacion}
                    onChange={(e) => setMotivoEliminacion(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    placeholder="Explique el motivo de la eliminación..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Escriba "{cotizacionParaEliminar.numero}" para confirmar
                  </label>
                  <input
                    type="text"
                    value={confirmacionNumero}
                    onChange={(e) => setConfirmacionNumero(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Número de cotización"
                  />
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Este es un <strong>borrado lógico</strong>. La cotización puede restaurarse desde "Registros Eliminados".
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEliminarModal(false);
                    setCotizacionParaEliminar(null);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarEliminacionCotizacion}
                  disabled={eliminando || motivoEliminacion.length < 10 || confirmacionNumero.trim().toUpperCase() !== cotizacionParaEliminar.numero.toUpperCase()}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {eliminando ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default QuotesList;
